import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import { sliderBottom } from "d3-simple-slider";
import {
  appendTimeSeriesTooltipOverlay,
  appendSignedVerticalAreaGradients,
  buildTooltipHtml,
  createZeroAnchoredArea,
  createTooltipHandlers,
  formatTooltipCurrency,
  formatTooltipDate,
  getNumericExtentIncludingZero,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const AesoPoolPriceVsEnmaxRetailLineGraph = ({
  poolPriceData,
  regulatedRateData,
  rateOfLastResortData,
  isLoading,
  initialStartDate,
  initialEndDate,
}) => {
  const svgRef = useRef();
  const sliderRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    if (!svg || !svg.node()) return;
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight - 20;
    const margin = { top: 40, right: 10, bottom: 70, left: 55 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const formatMonth = d3.timeFormat("%Y-%m");
    const poolColor = "#d48a4f";
    const regulatedColor = "springgreen";
    const rateOfLastResortColor = "violet";

    const averageByMonth = (data, dateField, valueField, multiplier = 1) => {
      const groupedData = d3.group(data, (d) => {
        const parsedDate = new Date(d[dateField]);
        if (!parsedDate || d[dateField] === null) {
          console.error(`Failed to parse date: ${d[dateField]}`);
          return null;
        }
        return formatMonth(parsedDate);
      });

      return Array.from(groupedData, ([key, values]) => {
        if (key === null) return null;
        return {
          marketDate: new Date(key),
          [valueField]: d3.mean(values, (d) => {
            const value = +d[valueField];
            if (isNaN(value)) {
              console.error(`Invalid ${valueField} value:`, d[valueField]);
              return null;
            }
            return value * multiplier;
          }),
        };
      }).filter((d) => d !== null);
    };

    const averagedRegulatedRateData = averageByMonth(
      regulatedRateData,
      "date",
      "price",
    );
    const averagedRateOfLastResortData = averageByMonth(
      rateOfLastResortData,
      "date",
      "unitPrice",
      10,
    );

    const minDate = new Date(
      Math.min(...averagedRegulatedRateData.map((x) => x.marketDate)),
    );

    const averagedPoolPriceData = averageByMonth(
      poolPriceData.filter((x) => new Date(x.marketDate) >= minDate),
      "marketDate",
      "poolPrice",
    );

    const differenceData = averagedPoolPriceData
      .map((poolPrice) => {
        const date = poolPrice.marketDate;
        const rateOfLastResort = averagedRateOfLastResortData.find(
          (d) => d.marketDate.getTime() === date.getTime(),
        );
        const regulatedRate = averagedRegulatedRateData.find(
          (d) => d.marketDate.getTime() === date.getTime(),
        );
        const comparePrice = rateOfLastResort
          ? rateOfLastResort.unitPrice
          : regulatedRate
            ? regulatedRate.price
            : null;
        return {
          marketDate: date,
          difference:
            comparePrice !== null ? poolPrice.poolPrice - comparePrice : null,
        };
      })
      .filter((d) => d.difference !== null);

    const lines = [
      {
        data: averagedPoolPriceData,
        color: poolColor,
        label: "Pool Price",
        valueKey: "poolPrice",
        strokeWidth: 2.4,
      },
      {
        data: averagedRegulatedRateData,
        color: regulatedColor,
        label: "Regulated Rate",
        valueKey: "price",
        strokeWidth: 2,
      },
      {
        data: averagedRateOfLastResortData,
        color: rateOfLastResortColor,
        label: "Rate of Last Resort",
        valueKey: "unitPrice",
        strokeWidth: 2,
      },
    ];

    const allDates = [...averagedPoolPriceData, ...differenceData];
    let currentLines = lines;
    let currentDifferenceData = differenceData;
    let tooltipDates = allDates.map((d) => ({ marketDate: d.marketDate }));

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(allDates, (d) => d.marketDate))
      .range([0, innerWidth]);

    const initialYValues = [
      ...averagedPoolPriceData.map((d) => d.poolPrice),
      ...averagedRegulatedRateData.map((d) => d.price),
      ...averagedRateOfLastResortData.map((d) => d.unitPrice),
      ...differenceData.map((d) => d.difference),
    ];

    const yScale = d3
      .scaleLinear()
      .domain(getNumericExtentIncludingZero(initialYValues))
      .nice()
      .range([innerHeight, 0]);

    const gridLines = chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const xAxis = chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .attr("class", "x-axis d3-chart-axis");

    const yAxis = chartSvg
      .append("g")
      .attr("class", "y-axis d3-chart-axis");

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    const linesGroup = chartSvg.append("g").attr("class", "lines");
    const areaPaths = new Map();

    lines.forEach((series) => {
      const gradients = appendSignedVerticalAreaGradients({
        chartSvg,
        idPrefix: `pool-retail-${series.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        color: series.color,
        positiveStops:
          series.label === "Rate of Last Resort"
            ? [
                { offset: "0%", opacity: 0.12 },
                { offset: "28%", opacity: 0.03 },
                { offset: "100%", opacity: 0 },
              ]
            : undefined,
        negativeStops:
          series.label === "Rate of Last Resort"
            ? [
                { offset: "0%", opacity: 0 },
                { offset: "72%", opacity: 0.03 },
                { offset: "100%", opacity: 0.12 },
              ]
            : undefined,
      });

      const positiveArea = createZeroAnchoredArea({
        valueAccessor: (d) => d[series.valueKey],
        xAccessor: (d) => d.marketDate,
        xScale,
        yScale,
      });
      const negativeArea = createZeroAnchoredArea({
        polarity: "negative",
        valueAccessor: (d) => d[series.valueKey],
        xAccessor: (d) => d.marketDate,
        xScale,
        yScale,
      });

      areaPaths.set(series.label, {
        negative: areaLayer
          .append("path")
          .datum(series.data)
          .attr("class", "d3-chart-area dashboard-area-highlight")
          .attr("fill", `url(#${gradients.negativeGradientId})`)
          .attr("d", negativeArea),
        positive: areaLayer
          .append("path")
          .datum(series.data)
          .attr("class", "d3-chart-area dashboard-area-highlight")
          .attr("fill", `url(#${gradients.positiveGradientId})`)
          .attr("d", positiveArea),
        positiveArea,
        negativeArea,
      });

      linesGroup
        .append("path")
        .datum(series.data)
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", series.color)
        .attr("stroke-width", series.strokeWidth)
        .attr(
          "d",
          d3
            .line()
            .defined((d) => Number.isFinite(d[series.valueKey]))
            .x((d) => xScale(d.marketDate))
            .y((d) => yScale(d[series.valueKey]))
            .curve(d3.curveMonotoneX),
        );
    });

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendTimeSeriesTooltipOverlay({
      chartSvg,
      innerWidth,
      innerHeight,
      xScale,
      getData: () => tooltipDates,
      xAccessor: (d) => d.marketDate,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) => {
        const dateKey = d.marketDate?.getTime?.();

        return buildTooltipHtml({
          title: formatTooltipDate(d.marketDate, "%Y-%m"),
          rows: [
            ...currentLines.map((series) => {
              const point = series.data.find(
                (value) => value.marketDate?.getTime?.() === dateKey,
              );

              return {
                label: series.label,
                value: formatTooltipCurrency(point?.[series.valueKey], 2),
                color: series.color,
              };
            }),
            {
              label: "Difference",
              value: formatTooltipCurrency(
                currentDifferenceData.find(
                  (value) => value.marketDate?.getTime?.() === dateKey,
                )?.difference,
                2,
              ),
              color: "#7abf7a",
            },
          ],
        });
      },
    });

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("AESO Pool Price vs Enmax Regulated Rates")
      .attr("class", "d3-chart-title");

    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Price ($/MWh)")
      .attr("class", "d3-chart-axis-label");

    const legendItems = [
      { color: poolColor, label: "Pool Price" },
      { color: regulatedColor, label: "Regulated Rate" },
      { color: rateOfLastResortColor, label: "Rate of Last Resort" },
      { color: "rgba(0, 255, 0, 0.5)", label: "Positive Difference" },
      { color: "rgba(255, 0, 0, 0.5)", label: "Negative Difference" },
    ];

    const legend = chartSvg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 10)
      .attr("text-anchor", "end")
      .attr("transform", `translate(${innerWidth}, ${-10})`)
      .attr("class", "d3-chart-legend");

    legendItems.forEach((item, i) => {
      const legendItem = legend
        .append("g")
        .attr("transform", `translate(${-i * 140}, 0)`);

      if (i < 3) {
        legendItem
          .append("line")
          .attr("x1", -30)
          .attr("x2", -10)
          .attr("stroke", item.color)
          .attr("stroke-width", item.label === "Pool Price" ? 2.4 : 2);
      } else {
        legendItem
          .append("rect")
          .attr("x", -30)
          .attr("y", -5)
          .attr("width", 20)
          .attr("height", 10)
          .attr("fill", item.color);
      }

      legendItem
        .append("text")
        .attr("x", -35)
        .attr("dy", "0.32em")
        .attr("class", "d3-chart-legend-label")
        .text(item.label);
    });

    const sliderSvg = d3
      .select(sliderRef.current)
      .attr("width", parentWidth)
      .attr("height", 50)
      .attr("class", "no-print")
      .attr("transform", "translate(0,-30)");

    sliderSvg.selectAll("*").remove();

    const sliderWidth = innerWidth;

    const updateChart = (range) => {
      const [startDate, endDate] = range;

      const filteredLines = lines.map((line) => ({
        ...line,
        data: line.data.filter(
          (d) => d.marketDate >= startDate && d.marketDate <= endDate,
        ),
      }));

      const filteredDifferenceData = differenceData.filter(
        (d) => d.marketDate >= startDate && d.marketDate <= endDate,
      );

      currentLines = filteredLines;
      currentDifferenceData = filteredDifferenceData;
      tooltipDates = Array.from(
        new Map(
          [...filteredLines.flatMap((line) => line.data), ...filteredDifferenceData].map(
            (d) => [d.marketDate.getTime(), { marketDate: d.marketDate }],
          ),
        ).values(),
      ).sort((a, b) => a.marketDate - b.marketDate);

      xScale.domain([startDate, endDate]);
      yScale
        .domain(
          getNumericExtentIncludingZero([
            ...filteredLines.flatMap((line) =>
              line.data.map((d) => d[line.valueKey]),
            ),
            ...filteredDifferenceData.map((d) => d.difference),
          ]),
        )
        .nice();

      const t = d3.transition().duration(50).ease(d3.easeLinear);

      gridLines
        .transition(t)
        .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

      xAxis
        .transition(t)
        .call(
          d3
            .axisBottom(xScale)
            .ticks(d3.timeMonth.every(3))
            .tickFormat(d3.timeFormat("%b %Y")),
        )
        .selectAll("text")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end");

      yAxis
        .transition(t)
        .call(d3.axisLeft(yScale).tickFormat((d) => `$${d.toFixed(0)}`));

      filteredLines.forEach((series) => {
        const paths = areaPaths.get(series.label);
        paths.positive
          .datum(series.data)
          .transition(t)
          .attr("d", paths.positiveArea);
        paths.negative
          .datum(series.data)
          .transition(t)
          .attr("d", paths.negativeArea);
      });

      linesGroup
        .selectAll(".line")
        .data(filteredLines)
        .join("path")
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", (d) => d.color)
        .attr("stroke-width", (d) => d.strokeWidth)
        .transition(t)
        .attr(
          "d",
          (d) =>
            d3
              .line()
              .defined((point) => Number.isFinite(point[d.valueKey]))
              .x((point) => xScale(point.marketDate))
              .y((point) => yScale(point[d.valueKey]))
              .curve(d3.curveMonotoneX)(d.data),
        );

      const numBars = filteredDifferenceData.length;
      const fixedPadding = 2;
      const barWidth = numBars > 0
        ? Math.max(0, (innerWidth - (numBars - 1) * fixedPadding) / numBars)
        : 0;

      chartSvg
        .selectAll(".bar")
        .data(filteredDifferenceData, (d) => d.marketDate)
        .join(
          (enter) =>
            enter
              .append("rect")
              .attr("class", (d) =>
                d.difference > 0
                  ? "bar dashboard-bar dashboard-bar-positive"
                  : "bar dashboard-bar dashboard-bar-negative")
              .attr("x", (d, i) => (numBars > 0 ? i * (barWidth + fixedPadding) : 0))
              .attr("y", (d) =>
                d.difference > 0 ? yScale(d.difference) : yScale(0),
              )
              .attr("width", barWidth)
              .attr("rx", 3)
              .attr("ry", 3)
              .attr("height", (d) => Math.abs(yScale(d.difference) - yScale(0)))
              .attr("fill", (d) =>
                d.difference > 0
                  ? "rgba(0, 255, 0, 0.5)"
                  : "rgba(255, 0, 0, 0.5)",
              ),
          (update) =>
            update.call((selection) =>
              selection
                .transition(t)
                .attr("x", (d, i) => (numBars > 0 ? i * (barWidth + fixedPadding) : 0))
                .attr("y", (d) =>
                  d.difference > 0 ? yScale(d.difference) : yScale(0),
                )
                .attr("width", barWidth)
                .attr("height", (d) => Math.abs(yScale(d.difference) - yScale(0))),
            ),
          (exit) => exit.transition(t).remove(),
        );
    };

    const throttle = (func, limit) => {
      let lastFunc;
      let lastRan;
      return function () {
        const context = this;
        const args = arguments;
        if (!lastRan) {
          func.apply(context, args);
          lastRan = Date.now();
        } else {
          clearTimeout(lastFunc);
          lastFunc = setTimeout(
            function () {
              if (Date.now() - lastRan >= limit) {
                func.apply(context, args);
                lastRan = Date.now();
              }
            },
            limit - (Date.now() - lastRan),
          );
        }
      };
    };

    const throttledUpdateChart = throttle(updateChart, 30);

    const slider = sliderBottom()
      .min(d3.min(allDates, (d) => d.marketDate))
      .max(d3.max(allDates, (d) => d.marketDate))
      .width(sliderWidth)
      .tickFormat(d3.timeFormat("%Y-%m-%d"))
      .ticks(5)
      .default([
        initialStartDate || d3.min(allDates, (d) => d.marketDate),
        initialEndDate || d3.max(allDates, (d) => d.marketDate),
      ])
      .fill("var(--theme-interactive-element)")
      .on("onchange", throttledUpdateChart);

    sliderSvg
      .append("g")
      .attr("transform", `translate(${(parentWidth - sliderWidth) / 2},10)`)
      .call(slider);

    sliderSvg.selectAll(".slider text").style("fill", "grey");
    sliderSvg.selectAll(".tick text").style("opacity", "0");

    updateChart([
      initialStartDate || d3.min(allDates, (d) => d.marketDate),
      initialEndDate || d3.max(allDates, (d) => d.marketDate),
    ]);
  }, [
    poolPriceData,
    regulatedRateData,
    rateOfLastResortData,
    initialStartDate,
    initialEndDate,
  ]);

  useEffect(() => {
    if (!isLoading) {
      renderChart();
    }
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  useEffect(() => {
    return () => {
      removeTooltip(tooltipRef);
    };
  }, []);

  if (isLoading ?? true) return <Loading />;
  return (
    <div className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}>
      <svg ref={svgRef} style={{ width: "100%" }}></svg>
      <svg ref={sliderRef} style={{ width: "100%" }}></svg>
    </div>
  );
};

export default AesoPoolPriceVsEnmaxRetailLineGraph;

